import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "space-y-4" }
const _hoisted_2 = { class: "p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 flex items-center justify-between" }
const _hoisted_3 = { class: "flex items-center space-x-3" }
const _hoisted_4 = { class: "w-10 h-10 rounded-xl bg-secondary/10 dark:bg-secondary-dark/20 text-secondary dark:text-secondary-dark-text flex items-center justify-center font-bold text-sm" }
const _hoisted_5 = { class: "font-bold text-sm text-gray-900 dark:text-white" }
const _hoisted_6 = { class: "text-xs text-gray-500 dark:text-gray-400" }
const _hoisted_7 = { class: "p-3.5 rounded-xl bg-blue-50/80 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-xs text-blue-800 dark:text-blue-300 flex items-start space-x-2.5" }
const _hoisted_8 = { class: "space-y-1" }
const _hoisted_9 = { class: "text-[11px] leading-relaxed opacity-90" }
const _hoisted_10 = {
  key: 0,
  class: "space-y-2 pt-1"
}
const _hoisted_11 = { class: "grid grid-cols-2 gap-2.5" }
const _hoisted_12 = { class: "flex-1 min-w-0" }
const _hoisted_13 = { class: "flex items-center justify-between" }
const _hoisted_14 = { class: "flex-1 min-w-0" }
const _hoisted_15 = { class: "flex items-center justify-between" }
const _hoisted_16 = { class: "space-y-2 pt-1" }
const _hoisted_17 = { class: "space-y-2 text-xs" }
const _hoisted_18 = { class: "flex items-center space-x-2.5 text-gray-700 dark:text-gray-300 cursor-pointer" }
const _hoisted_19 = {
  key: 0,
  class: "flex items-center space-x-2.5 text-gray-700 dark:text-gray-300 cursor-pointer"
}
const _hoisted_20 = { class: "flex items-center justify-between w-full" }

import { ref, watch } from 'vue'
import { BaseModal, BaseButton } from '@/components/common'
import type { ApplicationDetail } from '@/types/application'
import { Printer, ShieldCheck, Eye, EyeOff } from 'lucide-vue-next'

interface Props {
  isVisible: boolean
  application?: ApplicationDetail | null
  initialMaskSsn?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PrintConfirmModal',
  props: {
    isVisible: { type: Boolean },
    application: {},
    initialMaskSsn: { type: Boolean, default: true }
  },
  emits: ["close", "confirm"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const includeRemarks = ref(true)
const includeSignatures = ref(true)
const maskSsn = ref(props.initialMaskSsn ?? true)

watch(
  () => props.isVisible,
  (val) => {
    if (val) {
      maskSsn.value = props.initialMaskSsn ?? true
    }
  }
)

watch(
  () => props.initialMaskSsn,
  (val) => {
    if (val !== undefined) {
      maskSsn.value = val
    }
  }
)

const handleClose = () => {
  emit('close')
}

const handleConfirm = () => {
  emit('confirm', {
    includeRemarks: includeRemarks.value,
    includeSignatures: includeSignatures.value,
    maskSsn: maskSsn.value,
  })
}

const getInitials = (name?: string) => {
  if (!name) return 'AP'
  const parts = name.trim().split(' ')
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const formatStatus = (status?: string) => {
  if (!status) return 'Pending'
  if (status.toLowerCase() === 'reviewed') return 'Under Review'
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

const getStatusBadgeClass = (status?: string) => {
  switch (status) {
    case 'approved':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300'
    case 'reviewed':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
    case 'pending':
    default:
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(BaseModal), {
    "is-visible": _ctx.isVisible,
    title: "Confirm Print Application",
    subtitle: _ctx.application ? `Candidate: ${_ctx.application.full_name} (#${_ctx.application.application_id})` : '',
    icon: _unref(Printer),
    size: "md",
    type: "default",
    "show-footer": true,
    onClose: handleClose
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_20, [
        _createVNode(_unref(BaseButton), {
          variant: "outline",
          size: "md",
          text: "Cancel",
          onClick: handleClose
        }),
        _createVNode(_unref(BaseButton), {
          variant: "primary",
          size: "md",
          text: "Proceed to Print",
          icon: _unref(Printer),
          "icon-position": "left",
          onClick: handleConfirm
        }, null, 8, ["icon"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, _toDisplayString(getInitials(_ctx.application?.full_name)), 1),
            _createElementVNode("div", null, [
              _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.application?.full_name || 'Applicant Record'), 1),
              _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.application?.position_applied_for || 'Position Not Specified') + " • Ref #" + _toDisplayString(_ctx.application?.application_id), 1)
            ])
          ]),
          (_ctx.application?.application_status)
            ? (_openBlock(), _createElementBlock("span", {
                key: 0,
                class: _normalizeClass(["px-2.5 py-0.5 rounded-full text-xs font-bold", getStatusBadgeClass(_ctx.application.application_status)])
              }, _toDisplayString(formatStatus(_ctx.application.application_status)), 3))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(_unref(ShieldCheck), { class: "w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" }),
          _createElementVNode("div", _hoisted_8, [
            _cache[4] || (_cache[4] = _createElementVNode("p", { class: "font-semibold" }, "Confidential Document Notice", -1)),
            _createElementVNode("p", _hoisted_9, _toDisplayString(_ctx.application?.record_type === 'paper'
              ? 'This document contains confidential digitized personnel records and attachments. Ensure you are authorized to print and handle confidential personnel records.'
              : 'This document contains sensitive personal information (including background checks and tax data). Ensure you are authorized to print and handle confidential personnel records.'), 1)
          ])
        ]),
        (_ctx.application?.record_type !== 'paper')
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _cache[9] || (_cache[9] = _createElementVNode("label", { class: "block text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300" }, " Social Security Number (SSN) Display ", -1)),
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("button", {
                  type: "button",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (maskSsn.value = true)),
                  class: _normalizeClass(["p-2.5 rounded-xl border text-left transition-all duration-150 flex items-start space-x-2.5", maskSsn.value
              ? 'border-secondary dark:border-secondary-dark bg-secondary/5 dark:bg-secondary-dark/10 ring-1 ring-secondary dark:ring-secondary-dark'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800/60'])
                }, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["p-1 rounded-lg mt-0.5", maskSsn.value ? 'bg-secondary/15 text-secondary dark:text-secondary-dark-text' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'])
                  }, [
                    _createVNode(_unref(EyeOff), { class: "w-4 h-4" })
                  ], 2),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("span", {
                        class: _normalizeClass(["text-xs font-semibold", maskSsn.value ? 'text-secondary dark:text-secondary-dark-text' : 'text-gray-700 dark:text-gray-300'])
                      }, "Mask SSN", 2),
                      _createElementVNode("span", {
                        class: _normalizeClass(["w-2 h-2 rounded-full", maskSsn.value ? 'bg-secondary' : 'bg-transparent'])
                      }, null, 2)
                    ]),
                    _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-[11px] text-gray-500 dark:text-gray-400 font-mono mt-0.5" }, "***-**-XXXX", -1)),
                    _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-[10px] text-gray-400 dark:text-gray-500 mt-0.5" }, "Protected / Hidden", -1))
                  ])
                ], 2),
                _createElementVNode("button", {
                  type: "button",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (maskSsn.value = false)),
                  class: _normalizeClass(["p-2.5 rounded-xl border text-left transition-all duration-150 flex items-start space-x-2.5", !maskSsn.value
              ? 'border-amber-500 dark:border-amber-400 bg-amber-50/70 dark:bg-amber-950/20 ring-1 ring-amber-500 dark:ring-amber-400'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800/60'])
                }, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["p-1 rounded-lg mt-0.5", !maskSsn.value ? 'bg-amber-100 text-amber-600 dark:text-amber-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'])
                  }, [
                    _createVNode(_unref(Eye), { class: "w-4 h-4" })
                  ], 2),
                  _createElementVNode("div", _hoisted_14, [
                    _createElementVNode("div", _hoisted_15, [
                      _createElementVNode("span", {
                        class: _normalizeClass(["text-xs font-semibold", !maskSsn.value ? 'text-amber-700 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'])
                      }, "Reveal SSN", 2),
                      _createElementVNode("span", {
                        class: _normalizeClass(["w-2 h-2 rounded-full", !maskSsn.value ? 'bg-amber-500' : 'bg-transparent'])
                      }, null, 2)
                    ]),
                    _cache[7] || (_cache[7] = _createElementVNode("p", { class: "text-[11px] text-gray-500 dark:text-gray-400 font-mono mt-0.5" }, "XXX-XX-XXXX", -1)),
                    _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-[10px] text-gray-400 dark:text-gray-500 mt-0.5" }, "Visible / Unmasked", -1))
                  ])
                ], 2)
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_16, [
          _cache[12] || (_cache[12] = _createElementVNode("label", { class: "block text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300" }, " Additional Inclusions ", -1)),
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("label", _hoisted_18, [
              _withDirectives(_createElementVNode("input", {
                type: "checkbox",
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((includeRemarks).value = $event)),
                class: "w-4 h-4 rounded text-secondary focus:ring-secondary border-gray-300 dark:border-gray-600"
              }, null, 512), [
                [_vModelCheckbox, includeRemarks.value]
              ]),
              _cache[10] || (_cache[10] = _createElementVNode("span", null, "Include Administrator Review Notes & Decision", -1))
            ]),
            (_ctx.application?.record_type !== 'paper')
              ? (_openBlock(), _createElementBlock("label", _hoisted_19, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((includeSignatures).value = $event)),
                    class: "w-4 h-4 rounded text-secondary focus:ring-secondary border-gray-300 dark:border-gray-600"
                  }, null, 512), [
                    [_vModelCheckbox, includeSignatures.value]
                  ]),
                  _cache[11] || (_cache[11] = _createElementVNode("span", null, "Include Digital Signatures & Waivers", -1))
                ]))
              : _createCommentVNode("", true)
          ])
        ])
      ])
    ]),
    _: 1
  }, 8, ["is-visible", "subtitle", "icon"]))
}
}

})